import { ForwardedRef, forwardRef, RefObject, useEffect, useRef } from 'react';

import Focus from '@tiptap/extension-focus';
import styled from 'styled-components';

import { useAiInputAssistModal } from '@components/ai-input-assist/ai-input-modal';
import { AiInputProps } from '@components/ai-input-assist/types';
import { IconButton } from '@components/icon-button';
import { IconAddSmall } from '@components/icons/add-small';
import { IconAI } from '@components/icons/ai';
import { hideModal } from '@components/modal';
import {
  hideExtendedSidebar,
  showExtendedSidebar,
  useExtendedSidebarData,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  FieldMappingData,
  FieldMappingEditorValue,
  FieldMappingNode,
  FieldMappingVariables,
  FieldType,
  transformInput,
  transformOutput,
} from './addons/field-mapping';
import { FieldMappingSidebar, FieldMappingSidebarProps } from './field-mapping-sidebar';
import {
  FIELD_MAPPING_REST_MIN_HEIGHT_PX,
  FieldMappingContext,
  PILL_HEIGHT_PX,
  PILL_ROW_GAP_PX,
  pillRowAlignment,
} from './helpers';
import { BaseTextEditorProps, EditorMethods, TextEditor } from './text-editor';

export type BaseFieldMappingTextEditorProps = BaseTextEditorProps<{
  variables?: FieldMappingVariables;
  actions?: JSX.Element;
}> &
  FieldMappingData & {
    aiOptions?: AiInputProps;
    fieldType?: FieldType;
    hideManualInput?: boolean;
    /**
     * Whether date pills grow the format trigger. Defaults from fieldType: on for text
     * targets (date → text needs a display format), off when the target consumes the raw
     * value (e.g. a date picker).
     */
    hasFormatting?: boolean;
  };

export type FieldMappingTextEditorProps = BaseFieldMappingTextEditorProps & {
  defaultValue?: FieldMappingEditorValue;
  shouldShowFieldMappingOnFocus?: boolean;
  onManualSelect?: (value: any) => void;
  manualData?: any;
  shouldHideMenu?: boolean;
  customShowFieldMappingSidebar?: (props?: Pick<FieldMappingSidebarProps, 'variable'>) => void;
  isEditMode?: boolean;
  disableExpandable?: boolean;
};

const getActionsClassName = (editorUuid: string) => {
  return `field-mapping-actions-${editorUuid}`;
};

export const FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME = 'field-mapping-text-editor';

export const FieldMappingTextEditor = forwardRef(
  (
    {
      defaultValue,
      onChange,
      variableGroups,
      extensions,
      actions,
      aiOptions,
      fieldMappingId,
      fieldType = 'text',
      hasFormatting,
      hideManualInput = false,
      shouldHideMenu = false,
      shouldShowFieldMappingOnFocus = false,
      onManualSelect,
      manualData,
      className,
      customShowFieldMappingSidebar,
      isEditMode = true,
      disableExpandable = false,
      ...rest
    }: FieldMappingTextEditorProps,
    ref: ForwardedRef<EditorMethods>
  ) => {
    const editorUuid = useRef(uuid()).current;
    const { isExtended, extendedId } = useExtendedSidebarData();
    const editorRef = useRef<EditorMethods>(null);
    const finalRef = (ref ?? editorRef) as RefObject<EditorMethods>;
    const showAiInputAssistModal = useAiInputAssistModal();
    const parsedDefaultValue = defaultValue
      ? transformInput({ ...defaultValue, variableGroups })
      : undefined;
    const handleChange = (value: string) => {
      onChange?.(transformOutput(value));
    };

    const showFieldMappingSidebar = (props: Pick<FieldMappingSidebarProps, 'variable'> = {}) => {
      showExtendedSidebar({
        component: FieldMappingSidebar,
        props: {
          editorRef: finalRef,
          variableGroups: variableGroups ?? [],
          fieldType,
          hideManualInput,
          label: rest.label,
          fieldMappingId: fieldMappingId ?? '',
          onManualSelect,
          manualData,
          shouldHideMenu,
          ...props,
        },
        extendedId: editorUuid,
      });
    };
    const actionsId = getActionsClassName(editorUuid);
    const isThisOneExtended = isExtended && extendedId === editorUuid;

    // Group count alone misses fields loading into an already-present group.
    const variableGroupsSignature = variableGroups
      ?.map((group) => `${group.id}:${group.fields.length}`)
      .join('|');
    useEffect(() => {
      if (isThisOneExtended) {
        showFieldMappingSidebar();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variableGroupsSignature]);

    const Actions = actions ?? (
      <Stack orientation="horizontal" space={2} id={actionsId}>
        {aiOptions && (
          <IconButton
            icon={<IconAI size={16} />}
            size="sm"
            variant="aiGradient-white"
            onClick={() =>
              showAiInputAssistModal({
                ...aiOptions,
                plaintext: rest.plaintext,
                editorUuid,
                variableGroups,
                onSave: (value) => {
                  finalRef.current?.replaceContent(value);
                  handleChange(value);
                  hideModal();
                },
              })
            }
          />
        )}
        {variableGroups && (
          <IconButton
            icon={<IconAddSmall />}
            variant="blue-white"
            size="sm"
            dataTestid="field-mapping-open-picker"
            onClick={() => {
              !isExtended || (isExtended && extendedId !== editorUuid)
                ? showFieldMappingSidebar()
                : hideExtendedSidebar();
            }}
          />
        )}
      </Stack>
    );

    const contextShowFieldMappingSidebar = customShowFieldMappingSidebar ?? showFieldMappingSidebar;

    // rich (multi-line, formatted) field-mapping rows are PILL_HEIGHT + PILL_ROW_GAP tall with a
    // top pad of one gap, so 3 lines is 3 rows + that pad — not the base editor's 3 × 24px.
    const resolvedMinHeight =
      rest.minHeight ??
      (!rest.singleLine && !rest.plaintext ? pxToRem(FIELD_MAPPING_REST_MIN_HEIGHT_PX) : undefined);

    return (
      <FieldMappingContext.Provider
        value={{
          showFieldMappingSidebar: contextShowFieldMappingSidebar,
          variableGroups: variableGroups ?? [],
          isEditMode,
          hasFormatting: hasFormatting ?? ['text', 'paragraph'].includes(fieldType),
          disableExpandable,
        }}
      >
        <StyledEditor
          isActive={isThisOneExtended}
          className={classNames(FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME, className)}
          defaultValue={parsedDefaultValue}
          extensions={[
            FieldMappingNode,
            Focus.configure({ mode: 'deepest' }),
            ...(extensions ?? []),
          ]}
          ref={finalRef}
          actions={Actions}
          onChange={(data) => handleChange(data.value)}
          onFocus={() => {
            const actionsElement = document.querySelector(`#${actionsId}`);
            if (actionsElement && actionsElement.contains(document.activeElement)) {
              return;
            }
            if (
              (isExtended || shouldShowFieldMappingOnFocus) &&
              variableGroups &&
              extendedId !== editorUuid
            ) {
              showFieldMappingSidebar();
            }
          }}
          {...rest}
          minHeight={resolvedMinHeight}
        />
      </FieldMappingContext.Provider>
    );
  }
);

const StyledEditor = styled(TextEditor)(({ singleLine }) => {
  // Every field-mapping editor grows with content and gaps its pill rows: line-height carries the
  // PILL_ROW_GAP so a single pill never outgrows the line (no shift) and wrapped pills don't touch.
  // Single-line grows from one pill (minHeight); multi-line pads its top by the same gap.
  return {
    '.ProseMirror': {
      lineHeight: pxToRem(PILL_HEIGHT_PX + PILL_ROW_GAP_PX),
      ...(singleLine
        ? { minHeight: pxToRem(PILL_HEIGHT_PX) }
        : { paddingTop: pxToRem(PILL_ROW_GAP_PX) }),
    },
    '.ProseMirror-hideselection .has-focus': {
      '.placeholder-container': {
        filter: 'contrast(92%)',
      },
    },
    'ol, ul': {
      margin: 0,
      marginBottom: pxToRem(5),
      paddingInlineStart: pxToRem(25),
    },
    '.placeholder-container': {
      height: pxToRem(PILL_HEIGHT_PX),
    },
    '.node-wrapper': pillRowAlignment,
  };
});

export const NodeStyledFieldMappingTextEditor = styled(FieldMappingTextEditor)({
  cursor: 'pointer',
});
